import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { createLog, updateLog, submitLog, fetchLogById, clearCurrentLog } from '../store/slices/logsSlice';
import LoadingSpinner from '../components/LoadingSpinner';

export default function LogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLog, loading } = useSelector((state) => state.logs);
  const { user } = useSelector((state) => state.auth);
  const isEdit = !!id;

  const userRole = user?.role?.name || 'driver';
  const isDriver = userRole === 'driver';
  const isSupervisorOrAdmin = ['supervisor', 'admin'].includes(userRole);

  const [formData, setFormData] = useState({
    log_date: format(new Date(), 'yyyy-MM-dd'),
    shift_start: '',
    shift_end: '',
    start_odometer: '',
    end_odometer: '',
    fuel_used: '',
    fuel_cost: '',
    toll_cost: '',
    other_expenses: '',
    notes: '',
    weather_conditions: '',
    road_conditions: '',
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchLogById(id));
    }
    return () => dispatch(clearCurrentLog());
  }, [id, dispatch]);

  useEffect(() => {
    if (currentLog && isEdit) {
      setFormData({
        log_date: currentLog.log_date || '',
        shift_start: currentLog.shift_start || '',
        shift_end: currentLog.shift_end || '',
        start_odometer: currentLog.start_odometer || '',
        end_odometer: currentLog.end_odometer || '',
        fuel_used: currentLog.fuel_used || '',
        fuel_cost: currentLog.fuel_cost || '',
        toll_cost: currentLog.toll_cost || '',
        other_expenses: currentLog.other_expenses || '',
        notes: currentLog.notes || '',
        weather_conditions: currentLog.weather_conditions || '',
        road_conditions: currentLog.road_conditions || '',
      });
    }
  }, [currentLog, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        start_odometer: formData.start_odometer ? parseInt(formData.start_odometer) : null,
        end_odometer: formData.end_odometer ? parseInt(formData.end_odometer) : null,
        fuel_used: formData.fuel_used ? parseFloat(formData.fuel_used) : null,
        fuel_cost: formData.fuel_cost ? parseFloat(formData.fuel_cost) : null,
        toll_cost: formData.toll_cost ? parseFloat(formData.toll_cost) : null,
        other_expenses: formData.other_expenses ? parseFloat(formData.other_expenses) : null,
      };

      if (isEdit) {
        await dispatch(updateLog({ id, data })).unwrap();
        toast.success('Log updated successfully');
      } else {
        await dispatch(createLog(data)).unwrap();
        toast.success('Log created successfully');
      }
      navigate('/logs');
    } catch (error) {
      toast.error(error || 'Failed to save log');
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await dispatch(submitLog(id)).unwrap();
      toast.success('Log submitted for approval');
      navigate('/logs');
    } catch (error) {
      toast.error(error || 'Failed to submit log');
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Determine if user can edit this log
  const canEdit = !isEdit ||
    (isDriver && currentLog?.status === 'draft') ||
    (isSupervisorOrAdmin && currentLog?.status !== 'rejected');

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Log Entry' : 'New Log Entry'}
        </h1>
        {/* Only drivers can submit for approval */}
        {isEdit && isDriver && currentLog?.status === 'draft' && (
          <button
            onClick={handleSubmitForApproval}
            className="btn btn-primary"
          >
            Submit for Approval
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Log Date *
          </label>
          <input
            type="date"
            name="log_date"
            value={formData.log_date}
            onChange={handleChange}
            required
            disabled={!canEdit}
            className="w-full"
          />
        </div>

        {/* Shift Times */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shift Start
            </label>
            <input
              type="time"
              name="shift_start"
              value={formData.shift_start}
              onChange={handleChange}
              disabled={!canEdit}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shift End
            </label>
            <input
              type="time"
              name="shift_end"
              value={formData.shift_end}
              onChange={handleChange}
              disabled={!canEdit}
              className="w-full"
            />
          </div>
        </div>

        {/* Odometer */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Odometer (km)
            </label>
            <input
              type="number"
              name="start_odometer"
              value={formData.start_odometer}
              onChange={handleChange}
              disabled={!canEdit}
              min="0"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Odometer (km)
            </label>
            <input
              type="number"
              name="end_odometer"
              value={formData.end_odometer}
              onChange={handleChange}
              disabled={!canEdit}
              min="0"
              className="w-full"
            />
          </div>
        </div>

        {/* Fuel */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fuel Used (L)
            </label>
            <input
              type="number"
              name="fuel_used"
              value={formData.fuel_used}
              onChange={handleChange}
              disabled={!canEdit}
              step="0.01"
              min="0"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fuel Cost ($)
            </label>
            <input
              type="number"
              name="fuel_cost"
              value={formData.fuel_cost}
              onChange={handleChange}
              disabled={!canEdit}
              step="0.01"
              min="0"
              className="w-full"
            />
          </div>
        </div>

        {/* Expenses */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Toll Cost ($)
            </label>
            <input
              type="number"
              name="toll_cost"
              value={formData.toll_cost}
              onChange={handleChange}
              disabled={!canEdit}
              step="0.01"
              min="0"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Other Expenses ($)
            </label>
            <input
              type="number"
              name="other_expenses"
              value={formData.other_expenses}
              onChange={handleChange}
              disabled={!canEdit}
              step="0.01"
              min="0"
              className="w-full"
            />
          </div>
        </div>

        {/* Conditions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weather Conditions
            </label>
            <select
              name="weather_conditions"
              value={formData.weather_conditions}
              onChange={handleChange}
              disabled={!canEdit}
              className="w-full"
            >
              <option value="">Select...</option>
              <option value="clear">Clear</option>
              <option value="cloudy">Cloudy</option>
              <option value="rain">Rain</option>
              <option value="heavy_rain">Heavy Rain</option>
              <option value="fog">Fog</option>
              <option value="snow">Snow</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Road Conditions
            </label>
            <select
              name="road_conditions"
              value={formData.road_conditions}
              onChange={handleChange}
              disabled={!canEdit}
              className="w-full"
            >
              <option value="">Select...</option>
              <option value="good">Good</option>
              <option value="moderate">Moderate</option>
              <option value="poor">Poor</option>
              <option value="congested">Congested</option>
            </select>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            disabled={!canEdit}
            rows={4}
            className="w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/logs')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          {canEdit && (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" /> : isEdit ? 'Update' : 'Save Draft'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
